import { ComponentPropsWithoutRef, createContext, useContext } from 'react';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { twMerge } from 'tailwind-merge';

interface ITrackingStatusContext {
    activeStep: number;
}

const TrackingStatusContext = createContext<ITrackingStatusContext>({
    activeStep: 0,
});

interface TrackingStatusContainerProps extends ComponentPropsWithoutRef<'div'> {
    step: number;
}
const TrackingStatusContainer = (props: TrackingStatusContainerProps) => {
    const { className = '', children, step } = props;
    const { activeStep } = useContext(TrackingStatusContext);

    return (
        <div
            className={twMerge(
                'group flex flex-1 flex-col gap-2',
                step > activeStep && '[&>*]:opacity-70 [&_svg]:text-gray-400',
                className,
            )}
        >
            {children}
        </div>
    );
};

interface TrackingStatusIconProps
    extends Omit<ComponentPropsWithoutRef<'div'>, 'children'>,
        Pick<FontAwesomeIconProps, 'icon'> {}

const TrackingStatusIcon = (props: TrackingStatusIconProps) => {
    const { className = '', icon } = props;

    return (
        <div className={twMerge('relative w-full', className)}>
            <div
                className={
                    'transform-[translateY(-50%)] absolute right-0 top-1/2 h-px w-full bg-gray-200 group-last-of-type:hidden'
                }
            />
            <BoxIcon size="md" className={'relative'}>
                <FontAwesomeIcon icon={icon} />
            </BoxIcon>
        </div>
    );
};

interface TrackingStatusDescriptionContainerProps extends ComponentPropsWithoutRef<'div'> {}
const TrackingStatusStatusDescriptionContainer = (
    props: TrackingStatusDescriptionContainerProps,
) => {
    const { className = '', children } = props;

    return <div className={twMerge('flex max-w-[80%] flex-col', className)}>{children}</div>;
};

interface TrackingStatusDescriptionTitleProps extends ComponentPropsWithoutRef<'div'> {}

const TrackingStatusStatusDescriptionTitle = (props: TrackingStatusDescriptionTitleProps) => {
    const { className = '', children } = props;

    return <p className={twMerge('text-sm font-medium text-gray-800', className)}>{children}</p>;
};

interface TrackingStatusSubtitleProps extends ComponentPropsWithoutRef<'div'> {}

const TrackingStatusStatusSubtitle = (props: TrackingStatusSubtitleProps) => {
    const { className = '', children } = props;

    return <p className={twMerge('text-sm text-gray-800', className)}>{children}</p>;
};

const TrackingStatusProvider = TrackingStatusContext.Provider;
const Provider = TrackingStatusProvider;
const Root = TrackingStatusContainer;
const Icon = TrackingStatusIcon;
const DescriptionContainer = TrackingStatusStatusDescriptionContainer;
const DescriptionTitle = TrackingStatusStatusDescriptionTitle;
const DescriptionSubtitle = TrackingStatusStatusSubtitle;

export {
    TrackingStatusContext,
    //
    Provider,
    Root,
    Icon,
    DescriptionContainer,
    DescriptionTitle,
    DescriptionSubtitle,
    //
    TrackingStatusProvider,
    TrackingStatusContainer,
    TrackingStatusIcon,
    TrackingStatusStatusDescriptionContainer,
    TrackingStatusStatusDescriptionTitle,
    TrackingStatusStatusSubtitle,
};
